/**
 * §9.8 Global Contact Information — 6 primary channels + social row.
 */

import React from 'react'
import {
  Mail, LifeBuoy, Handshake, Megaphone, ShieldCheck, MapPin,
  Linkedin, Twitter, Youtube, Github, Sparkles, ArrowRight,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { CONTACT_CHANNELS, SOCIALS } from '../data'
import { TONE } from '../_shared'

const ICONS = { Mail, LifeBuoy, Handshake, Megaphone, ShieldCheck, MapPin }
const SOCIAL_ICONS = { Linkedin, Twitter, Youtube, Github }

export default function Global() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Global Contact Information</Eyebrow>
          <SectionHeading align="center"
            title={<>Reach the right desk{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">directly.</span></>}
            subtitle="Verified contact channels for sales, support, partnerships, press and legal. Route your enquiry to the right team from the start."
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {CONTACT_CHANNELS.map((c) => {
            const tone = TONE[c.tone] || TONE.orange
            const Icon = ICONS[c.iconKey] || Sparkles
            const isEmail = /@/.test(c.value)
            const href = isEmail ? `mailto:${c.value}` : undefined
            return (
              <a key={c.id} href={href} target={isEmail ? undefined : undefined}
                className="group relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-xl transition-all hover:border-white/20 hover:bg-white/[0.03] hover:-translate-y-[1px] overflow-hidden flex flex-col gap-3">
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 45% at 20% 0%, rgba(${tone.rgb},0.12), transparent 70%)` }} />
                <div className="relative flex items-center justify-between">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon strokeWidth={1.8} style={{ width: 18, height: 18 }} />
                  </span>
                  <span className="text-[10.5px] font-mono uppercase tracking-wider text-zinc-500">{c.label}</span>
                </div>
                <div className="relative">
                  <div className="text-[14.5px] font-semibold text-white leading-tight break-all">{c.value}</div>
                  <p className="text-[12px] text-zinc-400 leading-relaxed mt-1">{c.hint}</p>
                </div>
                {isEmail && (
                  <div className="relative mt-auto pt-1 inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-wider text-zinc-500 group-hover:text-brand-gold transition-colors">
                    Compose <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                )}
              </a>
            )
          })}
        </div>

        {/* Socials */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl px-5 md:px-6 py-4 flex flex-col md:flex-row items-start md:items-center gap-3">
          <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Follow us</div>
          <div className="flex flex-wrap items-center gap-2">
            {SOCIALS.map((s) => {
              const Icon = SOCIAL_ICONS[s.iconKey] || Sparkles
              return (
                <a key={s.id} href={s.href} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11.5px] font-semibold text-zinc-300 hover:text-white hover:border-white/20 transition-colors">
                  <Icon className="h-3 w-3 text-brand-gold" strokeWidth={2} />
                  {s.label}
                </a>
              )
            })}
          </div>
        </div>
      </Container>
    </section>
  )
}
